/**
 * Pricing Utilities
 * Centralized pricing calculation with discount and GST support
 * 
 * Calculation Order:
 * 1. Base Price
 * 2. Add GST (if applicable)
 * 3. Apply Discount on (Base + GST)
 * 4. Return Final Payable Amount
 */

import { supabase } from '../lib/supabase';

/**
 * Get active discount for a booking type
 * @param {string} bookingType - 'WELLNESS' or 'CONSULTATION'
 * @param {string} currentDate - Date in YYYY-MM-DD format (optional, defaults to today)
 * @returns {Promise<Object|null>} Active discount or null
 */
export async function getActiveDiscount(bookingType, currentDate = null) {
  try {
    const { data, error } = await supabase.rpc('get_active_discount', {
      p_booking_type: bookingType,
      p_current_date: currentDate || new Date().toISOString().split('T')[0]
    });

    if (error) {
      console.error('Error fetching active discount:', error);
      return null;
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Exception in getActiveDiscount:', error);
    return null;
  }
}

/**
 * Calculate final price with GST and discount
 * @param {number} basePrice - Base price of service/consultation
 * @param {string} bookingType - 'WELLNESS' or 'CONSULTATION'
 * @param {string} currentDate - Date in YYYY-MM-DD format (optional)
 * @returns {Promise<Object>} Price breakdown object
 */
export async function calculateFinalPrice(basePrice, bookingType, currentDate = null) {
  try {
    const { data, error } = await supabase.rpc('calculate_final_price', {
      p_base_price: basePrice,
      p_booking_type: bookingType,
      p_current_date: currentDate || new Date().toISOString().split('T')[0]
    });

    if (error) {
      console.error('Error calculating final price:', error);
      return getFallbackPricing(basePrice);
    }

    return data && data.length > 0 ? data[0] : getFallbackPricing(basePrice);
  } catch (error) {
    console.error('Exception in calculateFinalPrice:', error);
    return getFallbackPricing(basePrice);
  }
}

/**
 * Fallback pricing when database call fails
 * Returns base price without any modifications
 */
function getFallbackPricing(basePrice) {
  return {
    base_price: basePrice,
    gst_amount: 0,
    discount_percentage: 0,
    discount_amount: 0,
    discount_reason: null,
    final_amount: basePrice,
    has_gst: false,
    has_discount: false
  };
}

/**
 * Calculate total for multiple services (wellness booking)
 * @param {Array} services - Array of service objects with base_price
 * @param {string} currentDate - Date in YYYY-MM-DD format (optional)
 * @returns {Promise<Object>} Aggregated price breakdown
 */
export async function calculateWellnessTotal(services, currentDate = null) {
  if (!services || services.length === 0) {
    return getFallbackPricing(0);
  }

  const baseTotal = services.reduce((sum, service) => sum + (service.base_price || 0), 0);
  return await calculateFinalPrice(baseTotal, 'WELLNESS', currentDate);
}

/**
 * Calculate consultation fee with discount and GST
 * @param {number} consultationFee - Base consultation fee
 * @param {string} currentDate - Date in YYYY-MM-DD format (optional)
 * @returns {Promise<Object>} Price breakdown
 */
export async function calculateConsultationTotal(consultationFee, currentDate = null) {
  return await calculateFinalPrice(consultationFee, 'CONSULTATION', currentDate);
}

/**
 * Format price breakdown for display
 * @param {Object} priceBreakdown - Price breakdown from calculateFinalPrice
 * @returns {Object} Formatted price details for UI
 */
export function formatPriceBreakdown(priceBreakdown) {
  return {
    basePrice: parseFloat(priceBreakdown.base_price || 0).toFixed(2),
    gstAmount: parseFloat(priceBreakdown.gst_amount || 0).toFixed(2),
    gstPercentage: priceBreakdown.has_gst ? 
      ((priceBreakdown.gst_amount / priceBreakdown.base_price) * 100).toFixed(2) : null,
    discountPercentage: parseFloat(priceBreakdown.discount_percentage || 0).toFixed(2),
    discountAmount: parseFloat(priceBreakdown.discount_amount || 0).toFixed(2),
    discountReason: priceBreakdown.discount_reason,
    finalAmount: parseFloat(priceBreakdown.final_amount || 0).toFixed(2),
    hasGst: priceBreakdown.has_gst || false,
    hasDiscount: priceBreakdown.has_discount || false,
    savings: parseFloat(priceBreakdown.discount_amount || 0).toFixed(2)
  };
}

/**
 * Client-side price calculation (for immediate UI feedback)
 * Use this when you already have discount data and don't want to make API call
 * @param {number} basePrice - Base price
 * @param {Object} discount - Discount object with gst_percentage and discount_percentage
 * @returns {Object} Price breakdown
 */
export function calculatePriceClientSide(basePrice, discount = null) {
  let gstAmount = 0;
  let discountAmount = 0;
  let priceWithGst = basePrice;

  // Apply GST if exists
  if (discount && discount.gst_percentage) {
    gstAmount = (basePrice * discount.gst_percentage) / 100;
    priceWithGst = basePrice + gstAmount;
  }

  // Apply discount on (base + GST)
  if (discount && discount.discount_percentage) {
    discountAmount = (priceWithGst * discount.discount_percentage) / 100;
  }

  const finalAmount = priceWithGst - discountAmount;

  return {
    base_price: basePrice,
    gst_amount: gstAmount,
    discount_percentage: discount?.discount_percentage || 0,
    discount_amount: discountAmount,
    discount_reason: discount?.reason || null,
    final_amount: finalAmount,
    has_gst: gstAmount > 0,
    has_discount: discountAmount > 0
  };
}

